import React, { useState } from "react";
import type { AdminUser } from "../../authApi";
import { sendTestInvitationEmail } from "../../authApi";
import { apiErrorMessage } from "../../../utils/apiError";
import "./SendTestEmailCard.css";

interface Props {
  users: AdminUser[];
}

/** Site-admin utility: send the real branded invite email to an existing user
 *  (with a dead link, no account created) to test spam-filter deliverability. */
const SendTestEmailCard: React.FC<Props> = ({ users }) => {
  const [userId, setUserId] = useState("");
  const [sending, setSending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleSend = async () => {
    const target = users.find((u) => String(u.id) === userId);
    if (!target) return;
    setError(null);
    setNotice(null);
    setSending(true);
    try {
      const { email_result } = await sendTestInvitationEmail({
        email: target.email,
        name: target.name,
        tenant_id: target.tenants[0]?.id,
      });
      setNotice(
        email_result.sent
          ? `Test email sent to ${target.email}.`
          : email_result.error
          ? `Send failed: ${email_result.error}`
          : `Email not configured — would have sent to ${target.email} (check server logs).`
      );
    } catch (err: any) {
      setError(apiErrorMessage(err, "Failed to send test email"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="admin-section send-test-card">
      <h3>Send test to existing</h3>
      <p className="send-test-hint">
        Sends the real invite email (branded, with a dead link) to an existing
        user so you can check it clears their spam filter. No account is created.
      </p>
      {error && <div className="admin-error">{error}</div>}
      {notice && <div className="send-test-notice">{notice}</div>}
      <div className="send-test-row">
        <select value={userId} onChange={(e) => setUserId(e.target.value)}>
          <option value="">Select user...</option>
          {users.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name} ({u.email})
            </option>
          ))}
        </select>
        <button type="button" onClick={handleSend} disabled={sending || !userId}>
          {sending ? "Sending..." : "Send test to existing"}
        </button>
      </div>
    </div>
  );
};

export default SendTestEmailCard;
